// src/utils/dateUtils.ts

import { Lesson } from '../types';
import { zonedTimeToUtc, formatInTimeZone } from 'date-fns-tz';
import { format } from 'date-fns';

/**
 * Получить часовой пояс пользователя из браузера
 */
export const getUserTimeZone = (): string => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Получить смещение часового пояса в минутах относительно UTC.
 */
export const getTimezoneOffset = (timeZone: string, date: Date = new Date()): number => {
    try {
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        const parts = formatter.formatToParts(date);
        const getPart = (type: string) => parts.find(p => p.type === type)?.value || '0';

        const tzDateAsUTC = Date.UTC(
            parseInt(getPart('year')),
            parseInt(getPart('month')) - 1,
            parseInt(getPart('day')),
            parseInt(getPart('hour')) === 24 ? 0 : parseInt(getPart('hour')),
            parseInt(getPart('minute')),
            parseInt(getPart('second'))
        );

        return Math.round((tzDateAsUTC - date.getTime()) / 60000);
    } catch {
        return 0;
    }
};

/**
 * Получить момент времени начала урока как Date (в UTC).
 */
export const getLessonStartMoment = (lesson: Lesson): Date | null => {
    try {
        if (!lesson.date || !lesson.startTime) return null;

        const dateOnly = lesson.date.split('T')[0];
        const timeStr = lesson.startTime.slice(0, 5);
        const timezone = lesson.timezone || 'UTC';

        return zonedTimeToUtc(`${dateOnly} ${timeStr}:00`, timezone);
    } catch {
        return null;
    }
};

/**
 * Получить момент времени окончания урока как Date (в UTC).
 */
export const getLessonEndMoment = (lesson: Lesson): Date | null => {
    try {
        if (!lesson.date || !lesson.endTime) return null;

        const dateOnly = lesson.date.split('T')[0];
        const timeStr = lesson.endTime.slice(0, 5);
        const timezone = lesson.timezone || 'UTC';

        return zonedTimeToUtc(`${dateOnly} ${timeStr}:00`, timezone);
    } catch {
        return null;
    }
};

/**
 * Получить локальное время начала урока в формате "HH:mm"
 */
export const formatLessonStartTime = (lesson: Lesson): string => {
    try {
        const moment = getLessonStartMoment(lesson);
        if (!moment) return lesson.startTime?.slice(0, 5) || '';

        const userTimeZone = getUserTimeZone();
        return formatInTimeZone(moment, userTimeZone, 'HH:mm');
    } catch {
        return lesson.startTime?.slice(0, 5) || '';
    }
};

/**
 * Получить локальное время окончания урока в формате "HH:mm"
 */
export const formatLessonEndTime = (lesson: Lesson): string => {
    try {
        const moment = getLessonEndMoment(lesson);
        if (!moment) return lesson.endTime?.slice(0, 5) || '';

        const userTimeZone = getUserTimeZone();
        return formatInTimeZone(moment, userTimeZone, 'HH:mm');
    } catch {
        return lesson.endTime?.slice(0, 5) || '';
    }
};

/**
 * Получить интервал времени урока "HH:mm - HH:mm"
 */
export const formatLessonTimeRange = (lesson: Lesson): string => {
    return `${formatLessonStartTime(lesson)} - ${formatLessonEndTime(lesson)}`;
};

/**
 * Проверить, попадает ли урок в указанную дату (в timezone пользователя).
 */
export const isLessonOnDate = (lesson: Lesson, date: Date): boolean => {
    try {
        const moment = getLessonStartMoment(lesson);
        if (!moment) return false;

        const userTimeZone = getUserTimeZone();
        const localDateStr = formatInTimeZone(moment, userTimeZone, 'yyyy-MM-dd');
        const targetDateStr = format(date, 'yyyy-MM-dd');

        return localDateStr === targetDateStr;
    } catch {
        return false;
    }
};

/**
 * Конвертировать время из timezone урока в локальный timezone.
 */
export const convertLessonTime = (
    time: string,
    lessonTimeZone: string,
    dateForOffset: Date = new Date()
): string => {
    try {
        const userTimeZone = getUserTimeZone();

        if (userTimeZone === lessonTimeZone) {
            return time.slice(0, 5);
        }

        const lessonOffset = getTimezoneOffset(lessonTimeZone, dateForOffset);
        const userOffset = getTimezoneOffset(userTimeZone, dateForOffset);
        const diffMinutes = userOffset - lessonOffset;

        if (diffMinutes === 0) {
            return time.slice(0, 5);
        }

        const [hours, minutes] = time.split(':').map(Number);
        let totalMinutes = hours * 60 + minutes + diffMinutes;
        totalMinutes = ((totalMinutes % 1440) + 1440) % 1440;

        const newHours = Math.floor(totalMinutes / 60);
        const newMinutes = totalMinutes % 60;

        return `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    } catch {
        return time.slice(0, 5);
    }
};